import React, { useState, useEffect, forwardRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { API_BASE_URL, getAuthHeaders } from '../../config/api';
import { PlusIcon, PencilIcon, TrashIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const UniversityFeeStructureManagement = forwardRef((props, ref) => {
 const { token } = useAuth();
 const { showSuccess, showError } = useToast();
 const [fees, setFees] = useState([]);
 const [loading, setLoading] = useState(true);
 const [adding, setAdding] = useState(false);
 const [editing, setEditing] = useState(null);
 const [newFee, setNewFee] = useState({ category: '', amount: '', description: '' });

 useEffect(() => {
 fetchFeeStructure();
 }, []);

 const fetchFeeStructure = async () => {
 try {
 const response = await fetch(`${API_BASE_URL}/university/fee-structure`, {
 headers: getAuthHeaders(token)
 });
 if (response.ok) {
 const data = await response.json();
 setFees(data.fee_structure || []);
 }
 } catch (error) {
 console.error('Error fetching fee structure:', error);
 } finally {
 setLoading(false);
 }
 };

 const handleAddFee = async () => {
 if (!newFee.category.trim() || !newFee.amount.trim()) {
 showError('Category and amount are required');
 return;
 }

 setAdding(true);
 try {
 const response = await fetch(`${API_BASE_URL}/university/fee-structure`, {
 method: 'POST',
 headers: getAuthHeaders(token),
 body: JSON.stringify(newFee)
 });

 if (response.ok) {
 const data = await response.json();
 setFees([...fees, data.fee]);
 setNewFee({ category: '', amount: '', description: '' });
 showSuccess('Fee entry added successfully');
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to add fee entry');
 }
 } catch (error) {
 showError('Failed to add fee entry');
 } finally {
 setAdding(false);
 }
 };

 const handleUpdateFee = async (feeId, updatedData) => {
 try {
 const response = await fetch(`${API_BASE_URL}/university/fee-structure/${feeId}`, {
 method: 'PUT',
 headers: getAuthHeaders(token),
 body: JSON.stringify(updatedData)
 });

 if (response.ok) {
 const data = await response.json();
 setFees(fees.map(f => f.id === feeId ? data.fee : f));
 setEditing(null);
 showSuccess('Fee entry updated successfully');
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to update fee entry');
 }
 } catch (error) {
 showError('Failed to update fee entry');
 }
 };

 const handleDeleteFee = async (feeId) => {
 if (!window.confirm('Are you sure you want to delete this fee entry?')) return;

 try {
 const response = await fetch(`${API_BASE_URL}/university/fee-structure/${feeId}`, {
 method: 'DELETE',
 headers: getAuthHeaders(token)
 });

 if (response.ok) {
 setFees(fees.filter(f => f.id !== feeId));
 showSuccess('Fee entry deleted successfully');
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to delete fee entry');
 }
 } catch (error) {
 showError('Failed to delete fee entry');
 }
 };

 if (loading) {
 return (
 <div className="card relative overflow-hidden">
 <div className="text-center py-8">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
 <p className="mt-4 text-slate-500 text-sm font-semibold">Loading fee structure...</p>
 </div>
 </div>
 );
 }

 return (
 <div ref={ref} className="card relative overflow-hidden">
 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
 <div>
 <h3 className="text-lg font-bold text-slate-800">Fee Structure</h3>
 <p className="text-sm text-slate-500 mt-1">Manage your university's general fee structure (separate from program-specific fees)</p>
 </div>
 </div>

 {/* Add New Fee Form */}
 <div className="mb-6 p-5 bg-slate-50 border border-slate-200 rounded-2xl">
 <h4 className="text-sm font-bold text-slate-700 mb-3">Add New Fee Entry</h4>
 <div className="space-y-4">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Fee Category *</label>
 <input
 type="text"
 value={newFee.category}
 onChange={(e) => setNewFee({ ...newFee, category: e.target.value })}
 placeholder="e.g., Semester Fee, Tuition Fee, Lab Fee"
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Amount *</label>
 <input
 type="text"
 value={newFee.amount}
 onChange={(e) => setNewFee({ ...newFee, amount: e.target.value })}
 placeholder="e.g., PKR 50,000 or PKR 40,000 - 60,000"
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description (Optional)</label>
 <input
 type="text"
 value={newFee.description}
 onChange={(e) => setNewFee({ ...newFee, description: e.target.value })}
 placeholder="Additional details about this fee..."
 className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 />
 </div>
 <button
 onClick={handleAddFee}
 disabled={adding}
 className="btn-primary w-full sm:w-auto flex items-center justify-center space-x-2 py-2 px-5 text-sm disabled:opacity-50"
 >
 <PlusIcon className="h-4 w-4" />
 <span>{adding ? 'Adding...' : 'Add Fee Entry'}</span>
 </button>
 </div>
 </div>

 {/* Fee Structure List */}
 {fees.length > 0 ? (
 <div className="space-y-4">
 <h4 className="font-bold text-slate-700 text-sm mb-3">Current Fee Structure ({fees.length})</h4>
 
 {/* Mobile View - Cards */}
 <div className="block md:hidden space-y-3">
 {fees.map((fee) => (
 <div key={fee.id} className="bg-white border border-slate-200 rounded-2xl p-4 hover:border-primary-100 transition-all">
 {editing?.id === fee.id ? (
 <div className="space-y-3">
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Category</label>
 <input
 type="text"
 value={editing.category}
 onChange={(e) => setEditing({ ...editing, category: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Amount</label>
 <input
 type="text"
 value={editing.amount}
 onChange={(e) => setEditing({ ...editing, amount: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
 <input
 type="text"
 value={editing.description || ''}
 onChange={(e) => setEditing({ ...editing, description: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm"
 />
 </div>
 <div className="flex gap-2 pt-2 border-t border-slate-100">
 <button
 onClick={() => handleUpdateFee(fee.id, editing)}
 className="btn-primary py-1.5 px-4 text-xs sm:text-sm"
 >
 Save
 </button>
 <button
 onClick={() => setEditing(null)}
 className="btn-secondary py-1.5 px-4 text-xs sm:text-sm"
 >
 Cancel
 </button>
 </div>
 </div>
 ) : (
 <>
 <div className="mb-3">
 <div className="font-bold text-slate-800 text-sm">{fee.category}</div>
 <div className="text-sm font-bold text-emerald-600 mt-1">{fee.amount}</div>
 {fee.description && (
 <div className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">{fee.description}</div>
 )}
 </div>
 <div className="flex gap-2 pt-2 border-t border-slate-100">
 <button
 onClick={() => setEditing(fee)}
 className="flex-1 p-2 text-primary-600 hover:bg-primary-50 rounded-xl text-sm font-semibold flex items-center justify-center border border-slate-100 transition-all"
 >
 <PencilIcon className="h-4 w-4 mr-1" />
 Edit
 </button>
 <button
 onClick={() => handleDeleteFee(fee.id)}
 className="flex-1 p-2 text-red-500 hover:bg-red-50 rounded-xl text-sm font-semibold flex items-center justify-center border border-slate-100 transition-all"
 >
 <TrashIcon className="h-4.5 w-4.5 mr-1" />
 Delete
 </button>
 </div>
 </>
 )}
 </div>
 ))}
 </div>

 {/* Desktop View - Table */}
 <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200">
 <table className="min-w-full divide-y divide-slate-100 bg-white">
 <thead className="bg-slate-50">
 <tr>
 <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Category
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Amount
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Description
 </th>
 <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Actions
 </th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100 bg-white">
 {fees.map((fee) => (
 <tr key={fee.id} className="hover:bg-slate-50 transition-colors">
 {editing?.id === fee.id ? (
 <>
 <td className="px-6 py-4">
 <input
 type="text"
 value={editing.category}
 onChange={(e) => setEditing({ ...editing, category: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 bg-white text-slate-700"
 />
 </td>
 <td className="px-6 py-4">
 <input
 type="text"
 value={editing.amount}
 onChange={(e) => setEditing({ ...editing, amount: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 bg-white text-slate-700"
 />
 </td>
 <td className="px-6 py-4">
 <input
 type="text"
 value={editing.description || ''}
 onChange={(e) => setEditing({ ...editing, description: e.target.value })}
 className="w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 bg-white text-slate-700"
 />
 </td>
 <td className="px-6 py-4 text-right">
 <div className="flex justify-end space-x-2">
 <button
 onClick={() => handleUpdateFee(fee.id, editing)}
 className="btn-primary py-1.5 px-4 text-sm"
 >
 Save
 </button>
 <button
 onClick={() => setEditing(null)}
 className="btn-secondary py-1.5 px-4 text-sm"
 >
 Cancel
 </button>
 </div>
 </td>
 </>
 ) : (
 <>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="text-sm font-bold text-slate-800">{fee.category}</div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="text-sm font-bold text-emerald-600">{fee.amount}</div>
 </td>
 <td className="px-6 py-4">
 <div className="text-sm text-slate-500 font-medium">{fee.description || '-'}</div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
 <div className="flex justify-end space-x-2">
 <button
 onClick={() => setEditing(fee)}
 className="text-primary-600 hover:text-primary-700 p-2.5 rounded-xl hover:bg-primary-50 transition-all active:scale-95 border border-slate-100"
 title="Edit fee"
 >
 <PencilIcon className="h-4.5 w-4.5" />
 </button>
 <button
 onClick={() => handleDeleteFee(fee.id)}
 className="text-red-500 hover:text-red-700 p-2.5 rounded-xl hover:bg-red-50 transition-all active:scale-95 border border-slate-100"
 title="Delete fee"
 >
 <TrashIcon className="h-4.5 w-4.5" />
 </button>
 </div>
 </td>
 </>
 )}
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 ) : (
 <div className="text-center py-12 text-slate-400 bg-slate-50 border border-slate-100 rounded-2xl">
 <CurrencyDollarIcon className="h-16 w-16 mx-auto mb-4 text-slate-300 animate-pulse" />
 <p className="font-bold text-slate-700 text-lg">No Fee Structure Added</p>
 <p className="text-sm mt-1 max-w-sm mx-auto">Add fee categories to help students understand your university's costs.</p>
 <p className="text-xs mt-2 text-slate-400 max-w-sm mx-auto font-medium">Note: Program-specific fees are managed in the Programs tab.</p>
 </div>
 )}
 </div>
 );
});

UniversityFeeStructureManagement.displayName = 'UniversityFeeStructureManagement';

export default UniversityFeeStructureManagement;
